import { useEffect, useRef } from "react";

import { cn } from "../../lib/utils";

export interface TimeColumnItem<T extends string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface TimeColumnProps<T extends string | number> {
  items: TimeColumnItem<T>[];
  selected?: T;
  onSelect: (value: T) => void;
  ariaLabel: string;
  scrollKey?: number;
  className?: string;
}

export const TimeColumn = <T extends string | number>({
  items,
  selected,
  onSelect,
  ariaLabel,
  scrollKey = 0,
  className,
}: TimeColumnProps<T>) => {
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "center" });
  }, [scrollKey, selected]);

  return (
    <div
      role="listbox"
      aria-label={ariaLabel}
      data-slot="time-column"
      className={cn(
        "flex h-48 flex-col gap-0.5 py-1 pl-1 pr-0.5 overflow-y-auto",
        "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent",
        "hover:scrollbar-thumb-border",
        className
      )}
    >
      {items.map((item) => {
        const isSelected = item.value === selected;
        return (
          <button
            key={String(item.value)}
            ref={isSelected ? selectedRef : undefined}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={item.disabled}
            onClick={() => onSelect(item.value)}
            className={cn(
              "rounded-md px-2 py-1 text-sm tabular-nums transition-colors outline-none",
              "hover:bg-accent hover:text-accent-foreground",
              "focus-visible:bg-accent focus-visible:text-accent-foreground",
              "disabled:pointer-events-none disabled:opacity-40",
              isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};
